<template>
  <div class="location-field relative" @keydown.enter="onEnterKey" @focusout="onFocusOut">
    <PlacesAutocompleteField
      :model-value="value"
      :field-component="TextField"
      :field-props="fieldProps"
      :place-fields="PLACE_FIELDS"
      :region-codes="regionCodes"
      :included-primary-types="INCLUDED_PRIMARY_TYPES"
      :placeholder="placeholder"
      :clear-on-focus-value="clearOnFocusValue"
      :errors="[]"
      @update:model-value="onInput"
      @select="onSelect"
      @focus="$emit('focus')"
      @openChanged="$emit('openChanged', $event)"
    />
    <div v-if="allErrors.length" class="absolute left-1/2 bottom-0 w-0 h-0">
      <Tooltip :text="allErrors[0]" theme="warning" position="bottom" trigger="init" />
    </div>
  </div>
</template>

<script>
import { markRaw } from 'vue';
import { mapState } from 'vuex';

import { getPlaceBounds } from '@/services/places-address.js';

import Tooltip from '../Tooltip.vue';
import commonProps from './common-props-mixin';
import PlacesAutocompleteField from './PlacesAutocompleteField.vue';
import TextField from './TextField.vue';
import validation from './validation-mixin';

// Same filter as the legacy widget (`types: ['geocode']`). The Autocomplete (New) docs only
// advertise Table A/B types here, but 'geocode' is accepted and is the only value that
// reproduces the legacy result sets (streets and typo matches included; no padding with
// loosely related districts) - see TM-7461 QA comparison before changing it.
const INCLUDED_PRIMARY_TYPES = ['geocode'];

const PLACE_FIELDS = ['viewport'];

export default {
  name: 'LocationField',
  emits: ['change', 'focus', 'openChanged'],
  mixins: [validation, commonProps],
  components: { Tooltip, PlacesAutocompleteField },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      default: null,
    },
    /** Text cleared from the field on focus and restored when the field is left empty. */
    clearOnFocusValue: {
      type: String,
      default: null,
    },
    /** ISO code of the country the suggestions are limited to; the store's market when not given. */
    countryCode: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      TextField: markRaw(TextField),
      INCLUDED_PRIMARY_TYPES,
      PLACE_FIELDS,
      customErrors: [],
      value: this.modelValue, // value is the current scoped variable, modelValue is the prop
    };
  },

  computed: {
    ...mapState([
      'l10n',
    ]),
    regionCodes() {
      // Read the store only when no country is given: some apps using the field have no l10n in theirs.
      const countryCode = this.countryCode ?? this.l10n.ISOCountryCode;

      return countryCode ? [countryCode.toLowerCase()] : [];
    },
    fieldProps() {
      return {
        id: this.id,
        name: 'neighbourhood',
        type: 'text',
        autocomplete: 'off',
        ariaLabel: 'Location',
        icon: this.icon,
      };
    },
    allErrors() {
      return [...this.errors, ...this.customErrors];
    },
  },

  watch: {
    modelValue(modelValue) {
      this.value = modelValue;
    },
  },

  methods: {
    onInput(value) {
      this.value = value;
      this.hideError();
    },

    onFocusOut(event) {
      if (event.currentTarget.contains(event.relatedTarget)) {
        return;
      }
      if (this.clearOnFocusValue !== null && this.value === '' && this.modelValue === this.clearOnFocusValue) {
        this.value = this.modelValue;
        this.hideError();
      }
    },

    // The suggestions field prevents the default when Enter picks a suggestion; an
    // unprevented Enter means the typed text resolved to nothing, which the legacy
    // widget answered with the pick-from-the-list error.
    onEnterKey(event) {
      if (!event.defaultPrevented) {
        this.valueMatchesModelValue();
      }
    },

    onSelect({ place }) {
      const bounds = getPlaceBounds(place);

      if (!bounds) {
        this.showError();
        return;
      }

      this.hideError();
      this.$emit('change', {
        value: this.value,
        location: bounds,
      });
    },

    valueMatchesModelValue() {
      // It isn't matched, when a value is input but doesn't emit change event and pass the value to the parent component
      if (this.value !== this.modelValue) {
        this.showError();
        return false;
      }
      this.hideError();
      return true;
    },

    showError() {
      this.customErrors = ['Please select a location from the list'];
    },

    hideError() {
      this.customErrors = [];
    },
  },
};
</script>

<style lang="scss">
@use "@sass/base/variables";

.tooltip-red {
  .tooltip-arrow.tooltip-arrow {
    border-bottom-color: variables.$brand-danger;
  }
  .tooltip-inner.tooltip-inner {
    background-color: variables.$brand-danger;
  }
}
</style>
